import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface TokenOpportunity {
  id: string;
  token_symbol: string;
  token_name: string;
  token_id: string;
  collected_at: string;
  current_price: number;
  market_cap: number;
  volume_24h: number;
  price_change_24h: number | null;
  price_change_7d: number | null;
  opportunity_score: number;
  score_price_action: number | null;
  score_volume: number | null;
  score_fundamentals: number | null;
  score_sentiment: number | null;
  score_technicals: number | null;
  score_risk_adjusted: number | null;
  position_details: {
    supplyValueHealth?: {
      fdvRatioScore?: number;
      supplyCirculationScore?: number;
      athRecoveryScore?: number;
      undervaluationScore?: number;
    };
  } | null;
  buy_signals: Array<{
    type: string;
    strength: string;
    description: string;
  }>;
  narrative: string | null;
  risk_level: string | null;
  recommendation: string | null;
  categories: string[];
}

interface UseTopOpportunitiesOptions {
  limit?: number;
  minScore?: number;
  recommendation?: string;
  narrative?: string;
}

// Helper function to deduplicate tokens by symbol, keeping highest score
const deduplicateBySymbol = (data: TokenOpportunity[], maxItems: number): TokenOpportunity[] => {
  const uniqueTokens = new Map<string, TokenOpportunity>();
  for (const opp of data) {
    const existing = uniqueTokens.get(opp.token_symbol);
    if (!existing || opp.opportunity_score > existing.opportunity_score) {
      uniqueTokens.set(opp.token_symbol, opp);
    }
  }
  return Array.from(uniqueTokens.values())
    .sort((a, b) => b.opportunity_score - a.opportunity_score)
    .slice(0, maxItems);
};

export const useTopOpportunities = (options: UseTopOpportunitiesOptions = {}) => {
  const { limit = 20, minScore = 0, recommendation, narrative } = options;

  return useQuery({
    queryKey: ['top-opportunities', limit, minScore, recommendation, narrative],
    queryFn: async () => {
      // Fetch more rows to ensure enough unique tokens after deduplication
      const fetchLimit = limit * 3;
      
      // Only fetch from the latest collection_date to avoid stale/mixed data
      const { data: latestDate } = await supabase
        .from('token_opportunities')
        .select('collection_date')
        .order('collection_date', { ascending: false })
        .limit(1)
        .single();

      let query = supabase
        .from('token_opportunities')
        .select('*')
        .gte('opportunity_score', minScore)
        .order('opportunity_score', { ascending: false })
        .limit(fetchLimit);

      if (latestDate?.collection_date) {
        query = query.eq('collection_date', latestDate.collection_date);
      }

      if (recommendation) {
        query = query.eq('recommendation', recommendation);
      }

      if (narrative) {
        query = query.eq('narrative', narrative);
      }

      const { data, error } = await query;

      if (error) throw error;
      
      const mapped = (data || []).map(d => ({
        ...d,
        buy_signals: (d.buy_signals || []) as Array<{type: string; strength: string; description: string}>,
        categories: (d.categories || []) as string[]
      })) as TokenOpportunity[];
      
      // Deduplicate by token_symbol, keeping highest scoring entry
      return deduplicateBySymbol(mapped, limit);
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
  });
};

export const useOpportunityBySymbol = (symbol: string) => {
  return useQuery({
    queryKey: ['opportunity', symbol],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('token_opportunities')
        .select('*')
        .eq('token_symbol', symbol.toUpperCase())
        .order('collected_at', { ascending: false })
        .limit(1)
        .single();

      if (error) throw error;
      return {
        ...data,
        buy_signals: (data.buy_signals || []) as Array<{type: string; strength: string; description: string}>,
        categories: (data.categories || []) as string[]
      } as TokenOpportunity;
    },
    enabled: !!symbol,
    staleTime: 5 * 60 * 1000,
  });
};

export const useOpportunitiesByNarrative = (narrative: string) => {
  return useQuery({
    queryKey: ['opportunities-by-narrative', narrative],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('token_opportunities')
        .select('*')
        .eq('narrative', narrative)
        .order('opportunity_score', { ascending: false })
        .limit(30);

      if (error) throw error;
      
      const mapped = (data || []).map(d => ({
        ...d,
        buy_signals: (d.buy_signals || []) as Array<{type: string; strength: string; description: string}>,
        categories: (d.categories || []) as string[]
      })) as TokenOpportunity[];
      
      return deduplicateBySymbol(mapped, 10);
    },
    enabled: !!narrative,
    staleTime: 5 * 60 * 1000,
  });
};

export const useOpportunitiesByRecommendation = (recommendation: string) => {
  return useQuery({
    queryKey: ['opportunities-by-recommendation', recommendation],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('token_opportunities')
        .select('*')
        .eq('recommendation', recommendation)
        .order('opportunity_score', { ascending: false })
        .limit(45);

      if (error) throw error;
      
      const mapped = (data || []).map(d => ({
        ...d,
        buy_signals: (d.buy_signals || []) as Array<{type: string; strength: string; description: string}>,
        categories: (d.categories || []) as string[]
      })) as TokenOpportunity[];
      
      return deduplicateBySymbol(mapped, 15);
    },
    enabled: !!recommendation,
    staleTime: 5 * 60 * 1000,
  });
};
